import {
  FilterList,
  PlusOne,
  Search,
  SearchOff
} from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GavelIcon from "@mui/icons-material/Gavel";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import {
  Backdrop,
  Badge,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslationHook } from "../../hooks";
import { Procedure } from "../../types/model/procedure";

type ProcedureFilter = "total" | "active" | "ending" | null;

interface ProcedureListProps {
  procedures: Procedure[];
  loading?: boolean;
  onProcedureSelected?: (procedure: Procedure) => void;
  onAddProcedure?: () => void;
  onRefresh?: () => void;
  readOnly: boolean;
  activeFilter?: ProcedureFilter;
  onActiveFilterChange?: (filter: ProcedureFilter) => void;
}

const ProcedureList = ({
  procedures,
  loading = false,
  onProcedureSelected,
  onAddProcedure,
  onRefresh,
  readOnly,
  activeFilter = "total",
  onActiveFilterChange,
}: ProcedureListProps) => {
  const { t } = useTranslationHook();

  const [filteredList, setFilteredList] = useState<Procedure[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [search, setSearch] = useState("");

  const isActiveOnly = activeFilter === "active";
  const isEndingSoonOnly = activeFilter === "ending";

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isFilterOpen = Boolean(filterAnchorEl);

  useEffect(() => {
    let data = [...procedures];

    if (search) {
      const value = search.toLowerCase();
      data = data.filter((p) => p.name.toLowerCase().includes(value));
    }

    setFilteredList(data);
  }, [search, procedures]);

  const activeFilters = useMemo(() => {
    const list: { key: string; label: string }[] = [];

    if (isActiveOnly) {
      list.push({ key: "activeOnly", label: t("admin.procedures.filter-active-procedures") });
    }

    if (isEndingSoonOnly) {
      list.push({ key: "endingSoonOnly", label: t("admin.procedures.filter-ending-soon") });
    }

    if (search) {
      list.push({ key: "search", label: search });
    }

    return list;
  }, [isActiveOnly, isEndingSoonOnly, search, t]);

  const handleToggleActiveOnly = () => {
    onActiveFilterChange?.(isActiveOnly ? "total" : "active");
  };

  const handleToggleEndingSoonOnly = () => {
    onActiveFilterChange?.(isEndingSoonOnly ? "total" : "ending");
  };

  const handleChipDelete = (key: string) => {
    if (key === "search") {
      setSearch("");
    } else {
      onActiveFilterChange?.("total");
    }
  };

  const visibleRows = filteredList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>
        
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h5">
            {t("activities.tabs.procedures")}
          </Typography>

          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
              <Badge badgeContent={activeFilters.length} color="primary">
                <FilterList color={isFilterOpen ? "primary" : undefined} />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {!readOnly && (
            <Tooltip title={t("generic.add")}>
              <IconButton onClick={onAddProcedure}>
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        <TextField
          size="small"
          placeholder={t("admin.procedures.search-placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />

        {activeFilters.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            {activeFilters.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                onDelete={() => handleChipDelete(f.key)}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <Popover
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{ paper: { sx: { p: 3, width: 250 } } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t("generic.filter")}
            </Typography>

            <Chip
              label={t("admin.procedures.filter-active-procedures")}
              color={isActiveOnly ? "primary" : "default"}
              onClick={handleToggleActiveOnly}
            />

            <Chip
              label={t("admin.procedures.filter-ending-soon")}
              color={isEndingSoonOnly ? "primary" : "default"}
              onClick={handleToggleEndingSoonOnly}
            />
          </Box>
        </Popover>

        <TableContainer sx={{ flex: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("procedures.name")}</TableCell>
                <TableCell>{t("procedures.state")}</TableCell>
                <TableCell>{t("admin.projects.start-date")}</TableCell>
                <TableCell>{t("admin.projects.end-date")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("procedures.no-procedures")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((procedure) => {
                  const p = procedure as any;

                  const statusMap = () => {
                    if (p.canceled) return { label: t("procedures.canceled"), color: "error" as const, icon: <CancelIcon /> };
                    if (p.closed)   return { label: t("procedures.closed"),   color: "success" as const, icon: <CheckCircleIcon /> };
                    if (p.started)  return { label: t("procedures.started"),  color: "secondary" as const, icon: <TimelapseIcon /> };
                    return { label: t("procedures.to-be-started"), color: "default" as const, icon: <RadioButtonUncheckedIcon /> };
                  };

                  const status = statusMap();

                  const tooltipContent = (
                    <Box sx={{ p: 1, maxWidth: 280, display: "flex", flexDirection: "column", gap: 1 }}>
                      {p.description && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            {t("procedures.description")}
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {p.description}
                          </Typography>
                        </Box>
                      )}
                      {p.stageType && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            {t(`generic.stages.${p.stageType}`)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );

                  return (
                    <Tooltip
                      key={procedure.id}
                      title={tooltipContent}
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            width: 280,
                            maxWidth: 280,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: 3,
                            "& .MuiTooltip-arrow": {
                              color: "background.paper",
                              "&::before": { border: "1px solid", borderColor: "divider" },
                            },
                          },
                        },
                      }}
                    >
                      <TableRow
                        hover
                        onClick={() => onProcedureSelected?.(procedure)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box display="flex" gap={1.5}>
                            <GavelIcon sx={{ color: procedure.closed ? "gray" : "green" }} />
                            {procedure.name}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Chip
                            icon={status.icon}
                            label={status.label}
                            color={status.color}
                            size="small"
                            sx={{ borderRadius: "20px" }}
                          />
                        </TableCell>

                        <TableCell>
                          {procedure.startDate ? new Intl.DateTimeFormat("pt-PT").format(new Date(procedure.startDate)) : ""}
                        </TableCell>

                        <TableCell>
                          {procedure.endDate ? new Intl.DateTimeFormat("pt-PT").format(new Date(procedure.endDate)) : ""}
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default ProcedureList;
